import React from 'react';
import { Formik, Form, Field } from 'formik';
import {
  InputField,
  withFieldProps,
  TaggingFieldWithLabel,
  SelectFieldWithLabel,
  withLoading,
} from '@adjust/dashboard-components';
import { Account } from '@adjust/dashboard-core/api/types';
import {
  validate,
  isRequired,
  isEmail,
  areAllEmails,
  NO_MSG,
  ValidationMapper,
} from '@adjust/dashboard-core/lib/validations';

import { countryOptions } from '@react/constants/countries';
import { TimezoneOption } from '@redux/ducks/accountDetails';

const validation: ValidationMapper = {
  name: [[isRequired, () => t('field.company-name.error')]],
  invoiceEmails: [[areAllEmails, () => NO_MSG]],
  addressAttributesLine1: [[isRequired, () => t('field.address-line-1.error')]],
  city: [[isRequired, () => t('field.city.error')]],
  zipCode: [[isRequired, () => t('field.zip-code.error')]],
};

export interface AccountDetailsFormValues {
  id: number;
  data: Account;
}

export interface AccountDetailsFormProps {
  account: Account;
  timezones: TimezoneOption[];
  isLoading: boolean;
  onSubmit: (values: AccountDetailsFormValues) => void;
  onCancel: () => void;
}

const AccountDetailsFormBase: React.FC<AccountDetailsFormProps> = ({ account, timezones, onSubmit, onCancel }) => (
  <Formik
    initialValues={account}
    onSubmit={values => {
      onSubmit({
        id: values.id,
        data: values,
      });
    }}
  >
    {({ isValid, dirty }) => (
      <Form className="flex-one flex-box-column">
        <div className="flex-one panel-content">
          <Field name="name" validate={validate(validation.name)}>
            {withFieldProps(<InputField label={t('field.company-name')} />)}
          </Field>

          <Field name="invoice_emails" validate={validate(validation.invoiceEmails)}>
            {withFieldProps(
              <TaggingFieldWithLabel
                validation={isEmail}
                placeholder={t('field.type-emails')}
                label={t('field.invoice-emails')}
                info={t('field.invoice-emails.instructions')}
              />,
            )}
          </Field>

          <Field name="contact_phone_number">{withFieldProps(<InputField label={t('field.contact-phone')} />)}</Field>

          <Field name="timezone_id">
            {withFieldProps(
              <SelectFieldWithLabel
                options={timezones}
                placeholder={t('field.select-timezone')}
                label={t('field.time-zone')}
              />,
            )}
          </Field>

          <h4 className="field-heading">{t('label.invoices-address')}</h4>

          <Field name="address_attributes.line_1" validate={validate(validation.addressAttributesLine1)}>
            {withFieldProps(<InputField label={t('field.address-line-1')} />)}
          </Field>

          <Field name="address_attributes.line_2">
            {withFieldProps(<InputField label={t('field.address-line-2')} />)}
          </Field>

          <Field name="address_attributes.city" validate={validate(validation.city)}>
            {withFieldProps(<InputField label={t('field.city')} />)}
          </Field>

          <Field name="address_attributes.state">{withFieldProps(<InputField label={t('field.state')} />)}</Field>

          <Field name="address_attributes.zip_code" validate={validate(validation.zipCode)}>
            {withFieldProps(<InputField label={t('field.zip-code')} />)}
          </Field>

          <Field name="address_attributes.country_code">
            {withFieldProps(
              <SelectFieldWithLabel
                options={countryOptions}
                placeholder={t('label.select-country')}
                label={t('label.country')}
              />,
            )}
          </Field>

          <h4 className="field-heading">{t('label.company-details')}</h4>

          <Field name="address_attributes.company">
            {withFieldProps(<InputField label={t('field.company-name')} />)}
          </Field>

          <Field name="address_attributes.contact_person">
            {withFieldProps(<InputField label={t('field.contact-person')} />)}
          </Field>

          <Field name="vat_number">{withFieldProps(<InputField label={t('field.vat-number')} />)}</Field>

          <Field name="reference_number">{withFieldProps(<InputField label={t('field.reference-number')} />)}</Field>
        </div>

        <div className="panel-footer flex-auto">
          <div className="flex-box-row">
            <button type="button" className="btn flex-one" onClick={onCancel}>
              {t('action.cancel')}
            </button>
            <button type="submit" className="btn btn-main flex-one" disabled={!isValid || !dirty}>
              {t('action.save')}
            </button>
          </div>
        </div>
      </Form>
    )}
  </Formik>
);

export const AccountDetailsForm = withLoading(AccountDetailsFormBase);
